import React from 'react';
import { Mail, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const EmailStatsCard = ({ emailStats }) => {
    if (!emailStats) return null;

    return (
        <Card className="border-jungle/10 shadow-sm" data-testid="email-stats-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-jungle flex items-center gap-2 text-lg">
                    <Mail className="h-5 w-5 text-leaf" /> Statistiques Emails
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-leaf/5 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-jungle">{emailStats.this_week}</div>
                        <div className="text-xs text-muted-foreground mt-1">Cette semaine</div>
                    </div>
                    <div className="bg-leaf/5 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-jungle">{emailStats.this_month}</div>
                        <div className="text-xs text-muted-foreground mt-1">Ce mois</div>
                    </div>
                    <div className="bg-leaf/5 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-jungle">{emailStats.total}</div>
                        <div className="text-xs text-muted-foreground mt-1">Total</div>
                    </div>
                </div>
                {emailStats.daily?.length > 0 && (
                    <div className="h-48 mb-6">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={emailStats.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(142, 20%, 90%)" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(142, 20%, 60%)" />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(142, 20%, 60%)" />
                                <Tooltip />
                                <Bar dataKey="emails" fill="hsl(142, 60%, 40%)" radius={[4, 4, 0, 0]} name="Emails" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {emailStats.by_type && Object.keys(emailStats.by_type).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {Object.entries(emailStats.by_type).map(([type, count]) => (
                            <Badge key={type} variant="outline" className="border-jungle/20 text-jungle">
                                <Send className="h-3 w-3 mr-1" /> {type}: {count}
                            </Badge>
                        ))}
                    </div>
                )}
                {emailStats.recent?.length > 0 && (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {emailStats.recent.map(email => (
                            <div key={email.id} className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-gray-50/80">
                                <div className="flex-1 min-w-0">
                                    <span className="font-medium text-jungle truncate block">{email.to?.join(', ')}</span>
                                    <span className="text-xs text-muted-foreground truncate block">{email.subject}</span>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                    <Badge variant={email.status === 'sent' ? 'default' : 'destructive'}
                                        className={email.status === 'sent' ? 'bg-leaf/20 text-leaf border-0 text-[10px]' : 'text-[10px]'}>
                                        {email.status === 'sent' ? 'Envoye' : 'Erreur'}
                                    </Badge>
                                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                        {email.sent_at ? new Date(email.sent_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
